import { Auth0Provider } from '@auth0/auth0-react'
import { HelixProvider, ToastProvider } from '@helix-ds/react'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import { Provider } from 'react-redux'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import Layout from './components/Layout'
import ScrollToTop from './components/ScrollToTop'
import auth0Config from './features/auth/auth0/config/auth0'
import AuthGuard from './features/auth/components/AuthGuard'
import EntryPointGuard from './features/auth/components/EntryPointGuard'
import Login from './features/auth/components/Login'
import LoginCallback from './features/auth/components/LoginCallback'
import SentryUserSync from './features/auth/components/SentryUserSync'
import LocalizedAppShell from './i18n/LocalizedAppShell'
import PublicLocalizedShell from './i18n/PublicLocalizedShell'
import ConnectMedicalRecords from './pages/ConnectMedicalRecords'
import ConsentOptions from './pages/ConsentOptions'
import Dashboard from './pages/Dashboard'
import DOBCapture from './pages/DOBCapture'
import Home from './pages/Home'
import Intro from './pages/Intro'
import LanguageSelect from './pages/LanguageSelect'
import ManageConsents from './pages/ManageConsents'
import NotFound from './pages/NotFound'
import Profile from './pages/Profile'
import Results from './pages/Results'
import { store } from './store'

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <HelixProvider theme="family">
        <Auth0Provider {...auth0Config}>
          <SentryUserSync />
          <ScrollToTop />
          <ToastProvider>
            <Routes>
              <Route
                path="/health"
                element={
                  <PublicLocalizedShell>
                    <div>
                      <FormattedMessage id="healthcheck.greeting" defaultMessage="Hello from Family Portal" />
                    </div>
                  </PublicLocalizedShell>
                }
              />

              <Route
                path="/login"
                element={
                  <PublicLocalizedShell>
                    <Login />
                  </PublicLocalizedShell>
                }
              />
              <Route
                path="/login/callback"
                element={
                  <PublicLocalizedShell>
                    <LoginCallback />
                  </PublicLocalizedShell>
                }
              />

              <Route
                path="/"
                element={
                  <AuthGuard>
                    <LocalizedAppShell>
                      <Outlet />
                    </LocalizedAppShell>
                  </AuthGuard>
                }
              >
                <Route path="language-select" element={<LanguageSelect />} />
                <Route path="dob-capture" element={<DOBCapture />} />
                <Route
                  element={
                    <EntryPointGuard>
                      <Layout />
                    </EntryPointGuard>
                  }
                >
                  <Route index element={<Navigate to="/home" replace />} />
                  <Route path="home" element={<Home />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="intro" element={<Intro />} />
                  <Route path="results" element={<Results />} />
                  <Route path="profile" element={<Profile />} />
                  <Route path="consents/manage" element={<ManageConsents />} />
                  <Route path="health-records/connect" element={<ConnectMedicalRecords />} />
                  <Route path="consents/*" element={<ConsentOptions />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Route>
            </Routes>
          </ToastProvider>
        </Auth0Provider>
      </HelixProvider>
    </Provider>
  )
}

export default App
